/**
 * FlattenNode - flattens nested arrays to a 1D array
 *
 * Takes any depth of nested arrays and outputs a flat array of numbers.
 * Example: [[1,2,3],[4,5,6],[7,8,9]] -> [1,2,3,4,5,6,7,8,9]
 *
 * Endpoints:
 * - pattern (input): nested array to flatten
 * - flattened (output): flattened array of numbers
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { Dependencies } from '@nodes/Dependencies';
import { NodeDef } from '@runtime/IRuntime';
import { Payload } from '@graph/Payload';
import { params } from '@/nodes/TypedEndpoints';

const endpoints = params({
  pattern: { dataType: 'array', isInput: true, primary: true },
  flattened: { dataType: 'array', isOutput: true, allowInput: false },
});

/**
 * Recursively flatten any nested array structure to a 1D array of numbers
 */
function flatten(arr: any): number[] {
  if (!Array.isArray(arr)) {
    return typeof arr === 'number' ? [arr] : [];
  }
  const result: number[] = [];
  for (const item of arr) {
    if (Array.isArray(item)) {
      result.push(...flatten(item));
    } else if (typeof item === 'number') {
      result.push(item);
    }
  }
  return result;
}

export class FlattenNode extends BaseNode<typeof endpoints> {
  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();
    this.emitFlattened();
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    if (endpointId === 'pattern') {
      this.emitFlattened();
    }
  }

  private emitFlattened(): void {
    const input = this.params.pattern.value;
    const result = flatten(input);
    this.params.flattened.set(result);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const flattenNodeDef: NodeDef<FlattenNode> = {
  type: 'Flatten',
  shortName: 'flat',
  info: 'Flattens nested arrays to a 1D array of numbers.',
  domain: Domain.Data,
  endpoints,
  ctor: FlattenNode
};
